import React from 'react'
import { TouchableOpacity, Text, ActivityIndicator, type TouchableOpacityProps } from 'react-native'

interface ButtonProps extends TouchableOpacityProps {
  title: string
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  textColor?: string
  textClassName?: string
  /** Visually disabled actions may explain a missing prerequisite on tap. */
  onDisabledPress?: () => void
  /** Optional leading icon, drawn before the label. */
  icon?: React.ReactNode
}

// Theme-aware variants — paper / olive / blood semantic tokens.
// primary  = blood (main action; red on both light + dark)
// secondary = paper-up (subdued surface that contrasts with canvas)
// ghost    = transparent with text tint
// danger   = same as primary on the Battle Carp palette (blood IS the
//            warning color); kept as an alias so older call sites still work
const variantStyles = {
  primary:   { container: 'bg-blood',    text: 'text-paper font-stencil tracking-wider' },
  secondary: { container: 'bg-paper-up border border-olive', text: 'text-ink' },
  ghost:     { container: 'bg-transparent', text: 'text-ink-soft' },
  danger:    { container: 'bg-blood',    text: 'text-paper font-stencil tracking-wider' },
}

const sizeStyles = {
  sm: { container: 'px-3 py-1.5 rounded-lg', text: 'text-sm font-medium' },
  md: { container: 'px-4 py-3 rounded-xl', text: 'text-base font-semibold' },
  lg: { container: 'px-6 py-4 rounded-xl', text: 'text-lg font-bold' },
}

export function Button({ title, variant = 'primary', size = 'md', loading, disabled, textColor, textClassName, onDisabledPress, onPress, icon, ...props }: ButtonProps) {
  const v = variantStyles[variant]
  const s = sizeStyles[size]

  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={title}
      accessibilityState={{ disabled: !!(loading || (disabled && !onDisabledPress)), busy: !!loading }}
      className={`${v.container} ${s.container} items-center justify-center flex-row gap-2 ${
        disabled || loading ? 'opacity-50' : ''
      }`}
      disabled={loading || (disabled && !onDisabledPress)}
      onPress={disabled ? onDisabledPress : onPress}
      {...props}
    >
      {/* Loading: the spinner takes the label's place. The label stays laid out
          (invisible) so the button keeps its size and never wraps to two lines. */}
      {icon && !loading ? icon : null}
      <Text className={`${v.text} ${s.text}${textClassName ? ` ${textClassName}` : ''}`}
        style={[textColor ? { color: textColor } : null, loading ? { opacity: 0 } : null]}>{title}</Text>
      {loading && <ActivityIndicator size="small" color="white" style={{ position: 'absolute' }} />}
    </TouchableOpacity>
  )
}
